import {
  PriceTierListResponseType,
  SupplierRelationshipGetResponseType,
  SupplierRelationshipListResponseType,
  SupplierRelationshipUpdateRequestType,
} from "@sellar-io/api-v2/types";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  Button,
  H5,
  Icon,
  InputEmail,
  InputField,
  InputNote,
  InputText,
  InputTextArea,
} from "@sellar-io/components";
import { DialogCTA } from "../../../../components/modal/Dialog";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { EditPaymentTermsRadioSelector } from "./EditPaymentTermsRadioSelector";
import { useProductCatalogueMode } from "../../../../hooks/useProductCatalogueMode";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useAvailabilityTierList } from "../../../../hooks/api/v2/availabilityTier/useAvailabilityTierList";
import { InfoBadge } from "../../../../components/badge/InfoBadge";
import { useOrderRulesList } from "../../../../hooks/api/v2/orderRules/useOrderRulesList";
import { useCustomerZonesGetDefault } from "../../../../hooks/api/v2/customerZones/useCustomerZonesGetDefault";
import { Alert } from "../../../../components/alert/Alert";
import { useDeliveryOptions } from "../../../../hooks/useDeliveryOptions";
import { useFulfillmentTypeList } from "../../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { capitalize } from "@sellar-io/common";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import {
  getConnectionDeliveryAvailability,
  type ConnectionDeliveryState,
} from "./connectionDeliveryState";
import { useConnectionDeliveryState } from "./useConnectionDeliveryState";

export const EditConnectionPropertiesFragment = ({
  connection,
  priceTiers,
  buyerPostcode,
  buyerBusinessEmail,
  onSubmit,
  onDelete,
}: {
  onSubmit: (data: SupplierRelationshipUpdateRequestType) => void;
  connection?: SupplierRelationshipGetResponseType;
  priceTiers: PriceTierListResponseType;
  buyerPostcode?: string;
  onDelete?: () => void;
  buyerBusinessEmail?: string;
}) => {
  const { business } = useSellarCurrentBusiness();
  const productCatalogueMode = useProductCatalogueMode();

  const { isActive: remoteFlagActive } = useRemoteFeatureFlags();

  const unbundlingRelease2aEnabled = remoteFlagActive("unbundlingRelease2A");

  const unbundlingRelease2bEnabled = remoteFlagActive("unbundlingRelease2B");

  const unbundlingEnabled = remoteFlagActive("unbundlingReleaseFinal");

  const { data: defaultCustomerZone } = useCustomerZonesGetDefault(
    {
      buyerPostcode: buyerPostcode ?? "",
    },
    {
      enabled: (!connection || unbundlingEnabled) && !!buyerPostcode,
    },
  );

  const {
    isFetched: deliveryAreaIsFetched,
    deliveryOptions,
    deliveryAreas,
  } = useDeliveryOptions({
    postcode: buyerPostcode,
    hiddenOptions: ["ups"],
  });

  const legacyHasDirectArea = deliveryAreas?.some(
    (da) => !da.systemDefinedType,
  );
  const hasSDArea = deliveryAreas?.some(
    (da) => da.systemDefinedType === "sellarDistribution",
  );
  const allowsCollection = deliveryOptions.some(
    (option) => option.value === "collection",
  );
  const deliveryAvailability = getConnectionDeliveryAvailability({
    zone: defaultCustomerZone,
    hasSDArea: !!hasSDArea,
    allowsCollection,
  });
  const hasDirectDelivery = unbundlingEnabled
    ? deliveryAvailability?.direct
    : legacyHasDirectArea;

  const form = useForm<
    ConnectionDeliveryState & {
      buyerContactEmail: string;
      PriceTierId: number | null;
      AvailabilityTierId?: number | null;
      OrderRulesId?: number | null;
      DrayDeliveryMethodId?: number | null;
      CourierDeliveryMethodId?: number | null;
      PalletDeliveryMethodId?: number | null;
      vatPercentage: number;
    }
  >({
    shouldUseNativeValidation: true,
    defaultValues: {
      buyerContactEmail:
        connection?.buyerContactEmail ??
        buyerBusinessEmail ??
        connection?.Retailer.Business.email ??
        "",
      PriceTierId: connection?.PriceTierId ?? defaultCustomerZone?.PriceTierId,
      AvailabilityTierId:
        connection?.AvailabilityTierId ??
        defaultCustomerZone?.AvailabilityTierId,
      OrderRulesId:
        connection?.OrderRulesId ?? defaultCustomerZone?.OrderRulesId ?? null,
      DrayDeliveryMethodId: connection?.DrayDeliveryMethodId ?? null,
      CourierDeliveryMethodId: connection?.CourierDeliveryMethodId ?? null,
      PalletDeliveryMethodId: connection?.PalletDeliveryMethodId ?? null,
      drayDeliveryMethodVisible: !connection?.drayDeliveryMethodHiddenAt,
      courierDeliveryMethodVisible: !connection?.courierDeliveryMethodHiddenAt,
      palletDeliveryMethodVisible: !connection?.palletDeliveryMethodHiddenAt,
      fulfillmentType: connection?.fulfillmentType,
      vatPercentage:
        typeof connection?.vatPercentage === "number"
          ? connection.vatPercentage
          : 20,
    },
  });

  const formFulfillmentType = useWatch({
    control: form.control,
    name: "fulfillmentType",
  });
  const formPriceTierId = useWatch({
    control: form.control,
    name: "PriceTierId",
  });
  const formDrayDeliveryMethodVisible = useWatch({
    control: form.control,
    name: "drayDeliveryMethodVisible",
  });

  const isDrayForcedHidden =
    defaultCustomerZone?.type === "direct" &&
    formFulfillmentType === "courier" &&
    unbundlingEnabled;

  const formCourierDeliveryMethodVisible = useWatch({
    control: form.control,
    name: "courierDeliveryMethodVisible",
  });
  const formPalletDeliveryMethodVisible = useWatch({
    control: form.control,
    name: "palletDeliveryMethodVisible",
  });

  const formDrayDeliveryMethodId = useWatch({
    control: form.control,
    name: "DrayDeliveryMethodId",
  });
  const formCourierDeliveryMethodId = useWatch({
    control: form.control,
    name: "CourierDeliveryMethodId",
  });
  const formPalletDeliveryMethodId = useWatch({
    control: form.control,
    name: "PalletDeliveryMethodId",
  });

  const updateDeliveryState = useCallback(
    (state: ConnectionDeliveryState) => {
      if (state.fulfillmentType) {
        form.setValue("fulfillmentType", state.fulfillmentType);
      }
      form.setValue(
        "drayDeliveryMethodVisible",
        state.drayDeliveryMethodVisible,
      );
    },
    [form],
  );
  useConnectionDeliveryState({
    enabled: unbundlingEnabled,
    state: {
      fulfillmentType: formFulfillmentType,
      drayDeliveryMethodVisible: formDrayDeliveryMethodVisible,
      courierDeliveryMethodVisible: formCourierDeliveryMethodVisible,
      palletDeliveryMethodVisible: formPalletDeliveryMethodVisible,
    },
    availability: deliveryAvailability,
    isDirectZone: defaultCustomerZone?.type === "direct",
    onChange: updateDeliveryState,
  });

  const selectedPriceTier = priceTiers.find((pt) => pt.id === formPriceTierId);

  const [paymentDueCalculateFrom, setPaymentDueCalculateFrom] = useState<
    | SupplierRelationshipListResponseType[number]["paymentDueCalculateFrom"]
    | undefined
  >(connection?.paymentDueCalculateFrom ?? undefined);

  const [notes, setNotes] = useState<string>(connection?.notes ?? "");
  const [customerCode, setCustomerCode] = useState<string>(
    connection?.customerCode ?? "",
  );

  const [paymentTerms, setPaymentTerms] = useState<
    SupplierRelationshipListResponseType[number]["paymentTerms"] | undefined
  >(connection?.paymentTerms ? connection.paymentTerms : "proforma");

  const [paymentDue, setPaymentDue] = useState<number | undefined>(
    connection?.paymentDue ?? undefined,
  );
  const [syncedConnectionId, setSyncedConnectionId] = useState(
    connection?.id ?? null,
  );
  const connectionId = connection?.id ?? null;
  if (connectionId !== syncedConnectionId) {
    setSyncedConnectionId(connectionId);
    setPaymentTerms(
      connection?.paymentTerms ? connection.paymentTerms : "proforma",
    );
    setPaymentDueCalculateFrom(
      connection?.paymentDueCalculateFrom ?? undefined,
    );
    setPaymentDue(connection?.paymentDue ?? undefined);
  }

  const { data: availabilityTiersQuery, isFetched: availabilityTiersFetched } =
    useAvailabilityTierList({});
  const availabilityTiers = useMemo(
    () => availabilityTiersQuery?.pages.flat() ?? [],
    [availabilityTiersQuery?.pages],
  );

  const basicCatalogue = availabilityTiers.find(
    (tier) => tier.defaultType === "default",
  );
  const catalogueAssignmentReady =
    productCatalogueMode.isAdvancedMode ||
    (productCatalogueMode.isBasicMode && !!basicCatalogue);

  const { data: orderRulesQuery, isFetched: orderRulesFetched } =
    useOrderRulesList({});
  const orderRules = orderRulesQuery?.pages.flat() ?? [];

  const { data: deliveryMethodsQuery, isFetched: deliveryMethodsFetched } =
    useFulfillmentTypeList({}, { enabled: unbundlingEnabled });
  const deliveryMethods = deliveryMethodsQuery?.pages.flat() ?? [];

  const drayDeliveryMethods = deliveryMethods.filter(
    (deliveryMethod) => deliveryMethod.fulfillmentType === "direct",
  );
  const courierDeliveryMethods = deliveryMethods.filter(
    (deliveryMethod) => deliveryMethod.fulfillmentType === "courier",
  );
  const palletDeliveryMethods = deliveryMethods.filter(
    (deliveryMethod) => deliveryMethod.fulfillmentType === "haulier",
  );

  useEffect(() => {
    if (unbundlingEnabled && !defaultCustomerZone) return;
    if (!connection?.approved) {
      const isDirectZone = defaultCustomerZone?.type === "direct";
      const isDirectFulfillment = unbundlingEnabled
        ? hasDirectDelivery
        : isDirectZone || hasDirectDelivery;
      form.setValue(
        "fulfillmentType",
        isDirectFulfillment
          ? "direct"
          : hasSDArea
            ? "sellarDistribution"
            : "courier",
      );
      if (isDirectFulfillment) {
        form.setValue("drayDeliveryMethodVisible", true);
      }
    }
  }, [
    connection?.approved,
    defaultCustomerZone,
    form,
    hasDirectDelivery,
    hasSDArea,
    unbundlingEnabled,
  ]);

  useEffect(() => {
    if (connection) {
      form.setValue("AvailabilityTierId", connection.AvailabilityTierId);
      form.setValue("OrderRulesId", connection.OrderRulesId);
    }
  }, [connection, form]);

  useEffect(() => {
    if (defaultCustomerZone && !connection) {
      const defaultType =
        defaultCustomerZone.systemDefinedType === "sellarDistribution"
          ? "sellarDistribution"
          : defaultCustomerZone.type === "direct"
            ? "direct"
            : "default";

      const defaultPriceTierId = unbundlingEnabled
        ? (priceTiers.find((priceTier) => priceTier.defaultType === defaultType)
            ?.id ?? defaultCustomerZone.PriceTierId)
        : defaultCustomerZone.PriceTierId;
      const defaultAvailabilityTierId = unbundlingEnabled
        ? (availabilityTiers.find(
            (availabilityTier) => availabilityTier.defaultType === defaultType,
          )?.id ?? defaultCustomerZone.AvailabilityTierId)
        : defaultCustomerZone.AvailabilityTierId;

      form.setValue("PriceTierId", defaultPriceTierId);
      form.setValue("AvailabilityTierId", defaultAvailabilityTierId);
      form.setValue("OrderRulesId", defaultCustomerZone.OrderRulesId);
    }
  }, [
    defaultCustomerZone,
    buyerPostcode,
    form,
    connection,
    unbundlingEnabled,
    priceTiers,
    availabilityTiers,
  ]);

  const deliveryMethodTableCols =
    "grid grid-cols-[80px_minmax(0,1fr)_44px_48px] gap-2 px-3 py-3 sm:grid-cols-[100px_minmax(0,1fr)_60px_60px]";

  const showOrderRules =
    unbundlingRelease2aEnabled &&
    !!selectedPriceTier?.BasePriceTierId &&
    !unbundlingEnabled;

  return (
    <form
      onSubmit={form.handleSubmit((data) => {
        if (!catalogueAssignmentReady) {
          return;
        }

        const paymentInfo = {
          paymentTerms: paymentTerms,
          ...(paymentTerms === "credit"
            ? {
                paymentDue,
                paymentDueCalculateFrom,
              }
            : { paymentDue: null, paymentDueCalculateFrom: null }),
        };

        const vatPercentage = data.vatPercentage;

        const priceTier = priceTiers.find((pt) => pt.id === data.PriceTierId);

        const AvailabilityTierId = productCatalogueMode.isBasicMode
          ? basicCatalogue?.id
          : data.AvailabilityTierId;

        const OrderRulesId =
          unbundlingRelease2aEnabled && !!selectedPriceTier?.BasePriceTierId
            ? data.OrderRulesId
            : (priceTier?.OrderRulesId ?? null);

        onSubmit({
          ...data,
          ...paymentInfo,
          drayDeliveryMethodHiddenAt: data.drayDeliveryMethodVisible
            ? null
            : dayjs().toDate(),
          courierDeliveryMethodHiddenAt: data.courierDeliveryMethodVisible
            ? null
            : dayjs().toDate(),
          palletDeliveryMethodHiddenAt: data.palletDeliveryMethodVisible
            ? null
            : dayjs().toDate(),
          vatPercentage,
          notes,
          customerCode,
          AvailabilityTierId,
          OrderRulesId: OrderRulesId ? OrderRulesId : null,
        });
      })}
      className="flex flex-col gap-6"
    >
      <InputField
        className="gap-2"
        note={
          <InputNote>
            Your contact won’t be notified by email that you have added them to
            your customer list
          </InputNote>
        }
        label={"Buyer contact email"}
      >
        <InputEmail
          placeholder={"Email Address"}
          {...form.register("buyerContactEmail", { required: true })}
        />
      </InputField>
      <InputField
        className="gap-2"
        labelClassName="flex items-center gap-1"
        label={
          <>
            <span>
              {unbundlingRelease2bEnabled ? "Price list" : "Price tier"}
            </span>{" "}
            {unbundlingRelease2bEnabled && (
              <InfoBadge
                infoContent={{
                  title: "Price list",
                  body: (
                    <div className="flex flex-col gap-2">
                      <span>
                        Decide what pricing structure your customer sees on the
                        storefront
                      </span>
                      <Button
                        color="transparent"
                        className="flex items-center gap-2 pl-0 hover:bg-transparent"
                        onClick={() =>
                          window.open(
                            "https://intercom.help/sellario/en/articles/10148283-supplier-setup-pricing-and-discounts",
                            "_blank",
                          )
                        }
                      >
                        Find out more <Icon icon="CHEVRON_RIGHT" />
                      </Button>
                    </div>
                  ),
                }}
              />
            )}
          </>
        }
      >
        <InputSelect
          {...form.register("PriceTierId", {
            required: true,
            setValueAs: (value) => Number(value),
          })}
        >
          <InputOption value="" disabled hidden>
            Choose...
          </InputOption>
          {priceTiers
            .filter(
              (tier) =>
                !unbundlingRelease2bEnabled ||
                !connection?.hasDiscountsApplied ||
                tier.BasePriceTierId === null,
            )
            .map((tier) => (
              <option key={tier.id} value={tier.id}>
                {tier.name}
              </option>
            ))}
        </InputSelect>
      </InputField>
      {connection?.hasDiscountsApplied && unbundlingRelease2bEnabled && (
        <Alert
          type="warning"
          title="You can only assign this customer one of your migrated price tiers as they have customer-level discounts defined"
          message="To move this customer to new pricing, you can convert their discounts into a new derived price list, or replace their customer-level discounts with a new derived price list in the new Pricing area."
        />
      )}
      {productCatalogueMode.isAdvancedMode && (
        <InputField
          className="gap-2"
          labelClassName="flex items-center gap-1"
          label={
            <>
              <span>Product catalogue</span>{" "}
              <InfoBadge
                infoContent={{
                  title: "Product catalogue",
                  body: (
                    <div className="flex flex-col gap-2">
                      <span>
                        Decide which products your customer can and cannot see
                        on your storefront
                      </span>
                      <Button
                        color="transparent"
                        className="flex items-center gap-2 pl-0 hover:bg-transparent"
                        onClick={() =>
                          window.open(
                            "https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues ",
                            "_blank",
                          )
                        }
                      >
                        Find out more <Icon icon="CHEVRON_RIGHT" />
                      </Button>
                    </div>
                  ),
                }}
              />
            </>
          }
        >
          {availabilityTiersFetched && (
            <InputSelect
              {...form.register("AvailabilityTierId", {
                required: true,
                setValueAs: (value) => Number(value),
              })}
            >
              <InputOption value="" disabled hidden>
                Choose...
              </InputOption>
              {availabilityTiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.name}
                </option>
              ))}
            </InputSelect>
          )}
        </InputField>
      )}
      {showOrderRules && (
        <InputField
          className="gap-2"
          labelClassName="flex items-center gap-1"
          label={
            <>
              <span>Order rules</span>{" "}
              <InfoBadge
                infoContent={{
                  title: "Order rules",
                  body: (
                    <div className="flex flex-col gap-2">
                      <span>
                        Assign order rules independently from Pricing eg.
                        (minimum order price)
                      </span>
                      <Button
                        color="transparent"
                        className="flex items-center gap-2 pl-0 hover:bg-transparent"
                        onClick={() =>
                          window.open(
                            "https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues ",
                            "_blank",
                          )
                        }
                      >
                        Find out more <Icon icon="CHEVRON_RIGHT" />
                      </Button>
                    </div>
                  ),
                }}
              />
            </>
          }
        >
          {orderRulesFetched && (
            <InputSelect
              {...form.register("OrderRulesId", {
                setValueAs: (value) => Number(value),
              })}
            >
              <InputOption value="">None</InputOption>
              {orderRules.map((orderRule) => (
                <option key={orderRule.id} value={orderRule.id}>
                  {orderRule.name}
                </option>
              ))}
            </InputSelect>
          )}
        </InputField>
      )}

      {!unbundlingEnabled && (
        <InputField className="gap-2" label={"Delivery tier"}>
          {deliveryAreaIsFetched ? (
            <InputSelect
              {...form.register("fulfillmentType", { required: true })}
            >
              <InputOption value="" disabled hidden>
                Choose...
              </InputOption>
              {deliveryOptions.map(({ name, value }) => (
                <InputOption key={value} value={value}>
                  {name}
                </InputOption>
              ))}
            </InputSelect>
          ) : (
            <></>
          )}
        </InputField>
      )}

      {unbundlingEnabled && (
        <div className="flex flex-col gap-2">
          <H5 className="flex w-full items-center gap-1">
            <span>Delivery methods</span>
            <InfoBadge
              infoContent={{
                title: "Delivery methods",
                body: (
                  <div className="flex flex-col gap-2">
                    <span>
                      Decide which delivery methods are available to this
                      customer
                    </span>
                    <Button
                      color="transparent"
                      className="flex items-center gap-2 pl-0 hover:bg-transparent"
                      onClick={() =>
                        window.open(
                          "https://intercom.help/sellario/en/articles/13001221-supplier-guide-delivery-methods",
                          "_blank",
                        )
                      }
                    >
                      Find out more <Icon icon="CHEVRON_RIGHT" />
                    </Button>
                  </div>
                ),
              }}
            />
          </H5>
          <div className="divide-y overflow-hidden rounded-lg border border-zinc-300 text-xs">
            <div
              className={twMerge(
                "bg-zinc-50 font-semibold",
                deliveryMethodTableCols,
              )}
            >
              <div>Type</div>
              <div>Specific method</div>
              <div className="text-center">Visible</div>
              <div className="text-center">Default</div>
            </div>
            {hasSDArea && (
              <DeliveryMethodTableRow
                type="Sellar Distribution"
                selector={null}
                visible={null}
                defaultEl={
                  <input
                    type="checkbox"
                    checked={formFulfillmentType === "sellarDistribution"}
                    onChange={() =>
                      form.setValue("fulfillmentType", "sellarDistribution")
                    }
                  />
                }
                className={deliveryMethodTableCols}
              />
            )}
            {deliveryAvailability?.direct && (
              <DeliveryMethodTableRow
                type="Direct"
                selector={
                  deliveryMethodsFetched && (
                    <InputContainer
                      className={twMerge(
                        "min-w-0 py-2",
                        !formDrayDeliveryMethodVisible && "pointer-events-none",
                        formDrayDeliveryMethodId && "font-bold",
                      )}
                    >
                      <InputSelect
                        {...form.register("DrayDeliveryMethodId", {
                          setValueAs: (value) => (value ? Number(value) : null),
                          required: false,
                        })}
                      >
                        <option value="">
                          {(() => {
                            const inheritedMethod = drayDeliveryMethods.find(
                              (dm) =>
                                dm.id ===
                                defaultCustomerZone?.DrayDeliveryMethodId,
                            );
                            return inheritedMethod
                              ? `Inheriting from delivery zone (${inheritedMethod.name ?? capitalize(inheritedMethod.fulfillmentType)})`
                              : "Inheriting from delivery zone";
                          })()}
                        </option>
                        {drayDeliveryMethods
                          .filter(
                            (deliveryMethod) =>
                              deliveryMethod.id !==
                              defaultCustomerZone?.DrayDeliveryMethodId,
                          )
                          .map((deliveryMethod) => {
                            const displayName =
                              deliveryMethod.name ??
                              capitalize(deliveryMethod.fulfillmentType);
                            return (
                              <option
                                key={deliveryMethod.id}
                                value={deliveryMethod.id}
                              >
                                {displayName}
                              </option>
                            );
                          })}
                      </InputSelect>
                    </InputContainer>
                  )
                }
                visible={
                  <input
                    type="checkbox"
                    {...form.register("drayDeliveryMethodVisible")}
                    disabled={isDrayForcedHidden}
                  />
                }
                defaultEl={
                  <input
                    type="checkbox"
                    checked={formFulfillmentType === "direct"}
                    onChange={() => {
                      if (!formDrayDeliveryMethodVisible && !isDrayForcedHidden)
                        return;
                      form.setValue("drayDeliveryMethodVisible", true);
                      form.setValue("fulfillmentType", "direct");
                    }}
                  />
                }
                className={twMerge(
                  deliveryMethodTableCols,
                  !formDrayDeliveryMethodVisible && "opacity-50",
                )}
              />
            )}
            {defaultCustomerZone?.CourierDeliveryMethodId && (
              <DeliveryMethodTableRow
                type="Courier"
                selector={
                  deliveryMethodsFetched && (
                    <InputContainer
                      className={twMerge(
                        "min-w-0 py-2",
                        !formCourierDeliveryMethodVisible &&
                          "pointer-events-none",
                        formCourierDeliveryMethodId && "font-bold",
                      )}
                    >
                      <InputSelect
                        {...form.register("CourierDeliveryMethodId", {
                          setValueAs: (value) => (value ? Number(value) : null),
                          required: false,
                        })}
                      >
                        <option value="">
                          {(() => {
                            const inheritedMethod = courierDeliveryMethods.find(
                              (dm) =>
                                dm.id ===
                                defaultCustomerZone.CourierDeliveryMethodId,
                            );
                            return inheritedMethod
                              ? `Inheriting from delivery zone (${inheritedMethod.name ?? capitalize(inheritedMethod.fulfillmentType)})`
                              : "Inheriting from delivery zone";
                          })()}
                        </option>
                        {courierDeliveryMethods
                          .filter(
                            (deliveryMethod) =>
                              deliveryMethod.id !==
                              defaultCustomerZone.CourierDeliveryMethodId,
                          )
                          .map((deliveryMethod) => {
                            const displayName =
                              deliveryMethod.name ??
                              capitalize(deliveryMethod.fulfillmentType);
                            return (
                              <option
                                key={deliveryMethod.id}
                                value={deliveryMethod.id}
                              >
                                {displayName}
                              </option>
                            );
                          })}
                      </InputSelect>
                    </InputContainer>
                  )
                }
                visible={
                  <input
                    type="checkbox"
                    {...form.register("courierDeliveryMethodVisible")}
                  />
                }
                defaultEl={
                  <input
                    type="checkbox"
                    checked={formFulfillmentType === "courier"}
                    onChange={() => {
                      if (!formCourierDeliveryMethodVisible) return;
                      form.setValue("fulfillmentType", "courier");
                    }}
                  />
                }
                className={twMerge(
                  deliveryMethodTableCols,
                  !formCourierDeliveryMethodVisible && "opacity-50",
                )}
              />
            )}
            {defaultCustomerZone?.PalletDeliveryMethodId && (
              <DeliveryMethodTableRow
                type="Pallet"
                selector={
                  deliveryMethodsFetched && (
                    <InputContainer
                      className={twMerge(
                        "min-w-0 py-2",
                        !formPalletDeliveryMethodVisible &&
                          "pointer-events-none",
                        formPalletDeliveryMethodId && "font-bold",
                      )}
                    >
                      <InputSelect
                        {...form.register("PalletDeliveryMethodId", {
                          setValueAs: (value) => (value ? Number(value) : null),
                          required: false,
                        })}
                      >
                        <option value="">
                          {(() => {
                            const inheritedMethod = palletDeliveryMethods.find(
                              (dm) =>
                                dm.id ===
                                defaultCustomerZone.PalletDeliveryMethodId,
                            );
                            return inheritedMethod
                              ? `Inheriting from delivery zone (${inheritedMethod.name ?? capitalize(inheritedMethod.fulfillmentType)})`
                              : "Inheriting from delivery zone";
                          })()}
                        </option>
                        {palletDeliveryMethods
                          .filter(
                            (deliveryMethod) =>
                              deliveryMethod.id !==
                              defaultCustomerZone.PalletDeliveryMethodId,
                          )
                          .map((deliveryMethod) => {
                            const displayName =
                              deliveryMethod.name ??
                              capitalize(deliveryMethod.fulfillmentType);
                            return (
                              <option
                                key={deliveryMethod.id}
                                value={deliveryMethod.id}
                              >
                                {displayName}
                              </option>
                            );
                          })}
                      </InputSelect>
                    </InputContainer>
                  )
                }
                visible={
                  <input
                    type="checkbox"
                    {...form.register("palletDeliveryMethodVisible")}
                  />
                }
                defaultEl={
                  <input
                    type="checkbox"
                    checked={formFulfillmentType === "haulier"}
                    onChange={() => {
                      if (!formPalletDeliveryMethodVisible) return;
                      form.setValue("fulfillmentType", "haulier");
                    }}
                  />
                }
                className={twMerge(
                  deliveryMethodTableCols,
                  !formPalletDeliveryMethodVisible && "opacity-50",
                )}
              />
            )}
            {allowsCollection && (
              <DeliveryMethodTableRow
                type="Collection"
                selector={null}
                visible={null}
                defaultEl={
                  <input
                    type="checkbox"
                    checked={formFulfillmentType === "collection"}
                    onChange={() =>
                      form.setValue("fulfillmentType", "collection")
                    }
                  />
                }
                className={deliveryMethodTableCols}
              />
            )}
          </div>
          <InputNote>
            Any delivery method shown in <b>bold</b> is different to the
            delivery method specified in this customer's delivery zone:{" "}
            {defaultCustomerZone?.name ?? "Unknown"}
          </InputNote>
        </div>
      )}

      <InputField className="gap-2" label="VAT percentage">
        <InputSelect
          {...form.register("vatPercentage", {
            setValueAs: (value) => Number(value),
            required: false,
          })}
        >
          <InputOption value={20}>20%</InputOption>
          <InputOption value={0}>No VAT</InputOption>
        </InputSelect>
      </InputField>

      <div className="flex flex-col gap-2">
        <InputLabel>Payment terms</InputLabel>
        <EditPaymentTermsRadioSelector
          paymentTerms={paymentTerms}
          setPaymentTerms={(paymentTerms) => setPaymentTerms(paymentTerms)}
          setPaymentDueCalculateFrom={(paymentDueCalculateFrom) =>
            setPaymentDueCalculateFrom(paymentDueCalculateFrom)
          }
          paymentDueCalculateFrom={paymentDueCalculateFrom}
          paymentDue={paymentDue}
          setPaymentDue={(paymentDue) => setPaymentDue(paymentDue)}
          invoiceCreationStage={connection?.Supplier.invoiceCreationStage}
        />
      </div>

      <InputField className="gap-2" label={"Additional notes"}>
        <InputTextArea
          className="h-[100px] resize-none"
          required={false}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </InputField>
      <InputField className="gap-2" label={"Customer code"}>
        <InputText
          required={false}
          value={customerCode}
          onChange={(e) => setCustomerCode(e.target.value)}
        />
      </InputField>

      <DialogCTA className="gap-2 space-x-0 p-0">
        {onDelete && (
          <Button color={"red"} onClick={onDelete}>
            Delete
          </Button>
        )}
        <Button type={"submit"} disabled={!catalogueAssignmentReady}>
          {connection
            ? "Update terms"
            : business?.Supplier
              ? "Assign terms"
              : "Request terms"}
        </Button>
      </DialogCTA>
    </form>
  );
};

const DeliveryMethodTableRow = ({
  type,
  selector,
  visible,
  defaultEl,
  className,
}: {
  type: string;
  selector: ReactNode;
  visible: ReactNode;
  defaultEl: ReactNode;
  className?: string;
}) => {
  return (
    <div className={twMerge("min-h-12", className)}>
      <div className="flex items-center font-semibold">{type}</div>
      <div className="min-w-0">{selector}</div>
      <div className="flex items-center justify-center">{visible}</div>
      <div className="flex items-center justify-center">{defaultEl}</div>
    </div>
  );
};
